import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'Shop', href: '#merch' },
    { name: 'Crew', href: '#crew' },
    { name: 'Calendar', href: '#calendar' },
    { name: 'Surfing', href: '#surfing' },
    { name: 'Trips', href: '#trips' },
    { name: 'Competitions', href: '#competitions' },
    { name: 'Sponsorships', href: '#skating' } // This correctly points to skating section
  ];

  const scrollToSection = (href) => {
    console.log(`Attempting to scroll to: ${href}`);
    const element = document.querySelector(href);
    if (element) {
      console.log(`Element found:`, element);
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.error(`Element not found for href: ${href}`);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-orange-100'
          : 'bg-gradient-to-r from-black/20 via-black/10 to-black/20 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo Section */}
          <motion.div
            className="flex items-center"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <img
              src="/images/mainsite/logosurf.png"
              alt="FIU Surf Club Logo"
              className="h-24 lg:h-28 w-auto object-contain"// Increased height
            />
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item, index) => (
              <motion.button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg group ${
                  isScrolled
                    ? 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    : 'text-white hover:text-orange-300 hover:bg-white/10'
                }`}
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
              >
                {item.name}
                <motion.div
                  className={`absolute bottom-0 left-1/2 h-0.5 bg-orange-500 transform -translate-x-1/2 ${
                    isScrolled ? 'bg-orange-600' : 'bg-orange-400'
                  }`}
                  initial={{ width: 0 }}
                  whileHover={{ width: '80%' }}
                  transition={{ duration: 0.2 }}
                />
              </motion.button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <motion.button
              onClick={() => scrollToSection('#merch')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-200 ${
                isScrolled
                  ? 'bg-amber-700 text-white hover:bg-amber-800 shadow-md'
                  : 'bg-amber-600 text-white hover:bg-amber-700 shadow-lg'
              }`}
            >
              Merch
            </motion.button>
            
            <motion.a
              href="https://docs.google.com/forms/d/e/1FAIpQLSfiBjwcwiqWjNG6JABtm4GXLe4oDjTNi1nNIVM5u1l1eiQorQ/viewform"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-6 py-2 rounded-full font-semibold border-2 transition-all duration-200 ${
                isScrolled
                  ? 'border-stone-600 text-stone-600 hover:bg-stone-600 hover:text-white'
                  : 'border-white text-white hover:bg-white hover:text-stone-600'
              }`}
            >
              Join Us
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isScrolled 
                ? 'text-gray-700 hover:bg-orange-50' 
                : 'text-white hover:bg-white/10'
            }`}
            whileTap={{ scale: 0.95 }}
          >
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="h-6 w-6" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu className="h-6 w-6" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="lg:hidden bg-white/98 backdrop-blur-md border-t border-orange-100 shadow-lg"
          >
            <div className="px-4 py-6 space-y-4">
              {navItems.map((item, index) => (
                <motion.button
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  className="flex items-center w-full px-4 py-3 text-left text-gray-700 font-medium rounded-lg hover:bg-orange-50 hover:text-orange-600 transition-all duration-200 group"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.2 }}
                  whileHover={{ x: 5 }}
                >
                  <span className="flex-1">{item.name}</span>
                  <motion.div
                    className="w-2 h-2 bg-orange-500 rounded-full opacity-0 group-hover:opacity-100"
                    initial={{ scale: 0 }}
                    whileHover={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.button>
              ))}
              
              {/* Mobile Action Buttons */}
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <motion.button
                  onClick={() => scrollToSection('#merch')}
                  className="w-full py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Merch
                </motion.button>
                
                <motion.a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSfiBjwcwiqWjNG6JABtm4GXLe4oDjTNi1nNIVM5u1l1eiQorQ/viewform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 border-2 border-orange-600 text-orange-600 font-semibold rounded-lg hover:bg-orange-600 hover:text-white transition-all block text-center"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Join Us
                </motion.a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
